import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
    AgencyTripRow,
    AgencyTripsQuery,
    AgencyTripsSummary,
} from '@/integrations/supabase/rpc-types';

interface UseAgencyTripsResult {
    data: AgencyTripRow[];
    /** Righe della vista filtrata prima della paginazione. */
    totalCount: number;
    loading: boolean;
    /** Vero solo dai ricaricamenti successivi: la pagina resta a schermo mentre arriva. */
    refreshing: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/**
 * Una pagina della lista viaggi. Filtri e paginazione stanno nel database: la lista puo'
 * essere lunga e la tab carica solo cio' che mostra. La ricerca arriva gia' con il
 * debounce applicato dal chiamante, condiviso con i contatori.
 */
export function useAgencyTrips(query: AgencyTripsQuery): UseAgencyTripsResult {
    const [data, setData] = useState<AgencyTripRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useI18n();
    const loadedOnce = useRef(false);

    // Chiave stabile: l'oggetto query e' ricreato a ogni render del chiamante.
    const queryKey = JSON.stringify(query);

    const fetchData = useCallback(async () => {
        if (loadedOnce.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const result = await AgencyDashboardService.getTrips(JSON.parse(queryKey));
            setData(result);
            loadedOnce.current = true;
        } catch {
            const msg = String(t('errors.loadFailed'));
            setError(msg);
            toast({
                title: String(t('common.error')),
                description: msg,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        totalCount: data[0]?.total_count ?? 0,
        loading,
        refreshing,
        error,
        refresh: fetchData,
    };
}

/**
 * Contatori della tab Viaggi. Chiamata separata dalla lista perche' ignora la categoria:
 * cambiare contatore non deve ricaricarli, cambiare periodo, consulente o ricerca si'.
 */
export function useAgencyTripsSummary(
    query: Pick<AgencyTripsQuery, 'period' | 'designerId' | 'search'>
) {
    const [data, setData] = useState<AgencyTripsSummary | null>(null);
    const queryKey = JSON.stringify(query);

    const fetchData = useCallback(async () => {
        try {
            setData(await AgencyDashboardService.getTripsSummary(JSON.parse(queryKey)));
        } catch {
            // L'errore lo segnala gia' la lista, che parte in parallelo con gli stessi
            // filtri: un secondo toast direbbe la stessa cosa. I contatori restano vuoti.
            setData(null);
        }
    }, [queryKey]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return { data, refresh: fetchData };
}

export default useAgencyTrips;
